import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  IconButton,
  Avatar,
  Card,
  CardContent,
  Chip,
  Tooltip,
  Button,
  LinearProgress,
  alpha,
} from '@mui/material';
import {
  People as PeopleIcon,
  School as SchoolIcon,
  AssignmentOutlined as ReportIcon,
  Psychology as IntelIcon,
  SportsBasketball as TechIcon,
  LocalOffer as TagIcon,
  Visibility as VisibilityIcon,
  NavigateNext,
  NavigateBefore,
  PlayCircleOutline,
  CalendarToday,
  SportsBasketball,
  Article as NewsIcon,
  OpenInNew as OpenInNewIcon,
  Cake as CakeIcon,
  TrendingUp,
  EmojiEvents as TrophyIcon,
  ArrowForward,
  Straighten as MedidasIcon,
  LocalHospital as MedicalIcon,
  FolderOpen as DocumentsIcon,
} from '@mui/icons-material';
import api from '../api/axiosConfig';
import { cardBase } from '../theme/designSystem';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { Link, useNavigate } from 'react-router-dom';
import { useClubTheme } from '../contexts/ClubThemeContext';
import ReactPlayer from 'react-player';

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { primaryColor, secondaryColor } = useClubTheme();
  const avatarCacheBuster = useRef(new Date().getTime());

  const getPlayerAvatarUrl = (playerId) => {
    return playerId
      ? `${getMediaBaseUrl()}/photos/${playerId}.png?cache=${avatarCacheBuster.current}`
      : '/path/to/default-player-avatar.png';
  };

  // ── State ──────────────────────────────────────────────────
  const [stats, setStats] = useState({
    proCount: 0,
    canteraCount: 0,
    recentInformes: { count: 0, players: [] },
    recentIntel: { count: 0, players: [] },
    recentTech: { count: 0, players: [] },
    recentMedidas: { count: 0, players: [] },
    recentMedical: { count: 0, players: [] },
    recentDocuments: { count: 0, players: [] },
    bigBoardTagCount: 0,
    seguimientoTagCount: 0,
  });
  const [canteraPhotos, setCanteraPhotos] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [videos, setVideos] = useState([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [topGlobal, setTopGlobal] = useState([]);
  const [news, setNews] = useState([]);
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  const [birthdayPlayers, setBirthdayPlayers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredStat, setHoveredStat] = useState(null);

  const mediaUrl = getMediaBaseUrl();

  // Restriction par type selon le rôle: un utilisateur cantera ne voit que la cantera,
  // un admin pro que le pro. Détermine quelles cartes KPI joueurs afficher.
  const userRole = (JSON.parse(localStorage.getItem('user') || 'null')?.role || '').toLowerCase();
  const restrictedType =
    userRole === 'admin pro'
      ? 'pro'
      : (['admin cantera', 'scout interne cantera', 'scout externe cantera'].includes(userRole) ? 'cantera' : null);

  const currentUrl = window.location.href.toLowerCase();
  const isKansas = currentUrl.includes('kansas');
  const isMetropolitans = currentUrl.includes('metropolitans');
  const isByers = currentUrl.includes('byers');
  const dashboardLogoPath = isKansas ? '/assets/kansas.png'
    : isMetropolitans ? '/assets/metropolitans.png'
    : isByers ? '/assets/byers.png'
    : '/assets/panneau.png';

  const shuffleArray = (array) => {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  // ── Data fetching ──────────────────────────────────────────
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [dashRes, videosRes, newsRes] = await Promise.all([
          api.get('/api/dashboard'),
          api.get('/api/videos/with-player').catch(() => ({ data: [] })),
          api.get('/api/news').catch(() => ({ data: [] })),
        ]);

        setStats({
          proCount: dashRes.data.proCount,
          canteraCount: dashRes.data.canteraCount,
          recentInformes: dashRes.data.recentInformes,
          recentIntel: dashRes.data.recentIntel,
          recentTech: dashRes.data.recentTech,
          recentMedidas: dashRes.data.recentMedidas,
          recentMedical: dashRes.data.recentMedical,
          recentDocuments: dashRes.data.recentDocuments,
          bigBoardTagCount: dashRes.data.bigBoardTagCount,
          seguimientoTagCount: dashRes.data.seguimientoTagCount,
        });

        if (dashRes.data.birthdayPlayers) {
          setBirthdayPlayers(dashRes.data.birthdayPlayers);
        }

        const photoIds = shuffleArray(Array.from({ length: 17 }, (_, i) => i + 1));
        setCanteraPhotos(photoIds.map((id) => ({ id, photo: `${mediaUrl}/summits/${id}.png` })));

        setVideos(videosRes.data);
        setNews(newsRes.data);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading dashboard:', error);
        setIsLoading(false);
      }
    };

    fetchAll();
  }, [mediaUrl]);

  // Top global
  useEffect(() => {
    const fetchTopGlobal = async () => {
      try {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const journee = yesterday.toISOString().split('T')[0];
        const clubId = localStorage.getItem('clubId');
        const endpoint = clubId ? '/api/resume-journee/v2/top-global' : '/api/resume-journee/top-global';
        const params = clubId ? { club_id: clubId, journee } : { journee };
        const res = await api.get(endpoint, { params });
        setTopGlobal(res.data);
      } catch (error) {
        console.error('Error loading top global:', error);
      }
    };
    fetchTopGlobal();
  }, []);

  // ── Auto-play timers ───────────────────────────────────────
  useEffect(() => {
    if (canteraPhotos.length > 1) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => {
          let next;
          do { next = Math.floor(Math.random() * canteraPhotos.length); } while (next === prev);
          return next;
        });
      }, 4000);
      return () => clearInterval(timer);
    }
  }, [canteraPhotos]);

  useEffect(() => {
    if (news.length > 1) {
      const timer = setInterval(() => {
        setCurrentNewsIndex((prev) => (prev + 1) % news.length);
      }, 6000);
      return () => clearInterval(timer);
    }
  }, [news]);

  useEffect(() => {
    if (videos.length > 1) {
      const timer = setInterval(() => {
        setCurrentVideoIndex((prev) => (prev + 1) % videos.length);
      }, 8000);
      return () => clearInterval(timer);
    }
  }, [videos]);

  // ── Helpers ────────────────────────────────────────────────
  const typeLabels = {
    'EVALUATION': t('dashboard.topStats.evaluation'),
    'POINTS': t('dashboard.topStats.points'),
    'REBONDS': t('dashboard.topStats.rebounds'),
    'PASSES': t('dashboard.topStats.assists'),
    'STEALS': t('dashboard.topStats.steals'),
    'BLOCKS': t('dashboard.topStats.blocks'),
    'THREE_PM': t('dashboard.topStats.threePointers'),
    'GAME_SCORE': t('dashboard.topStats.gameScore'),
    'TRUE_SHOOTING': t('dashboard.topStats.trueShooting'),
  };

  const getTopPlayerAvatar = (joueur) => {
    if (!joueur) return '';
    // Joueur local: photo via ID
    if (joueur.infos?.local_id) {
      return `${mediaUrl}/photos/${joueur.infos.local_id}.png?cache=${avatarCacheBuster.current}`;
    }
    // Joueur externe: photo via proxy backend
    if (joueur.infos?.photo) {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      return `${backendUrl}/api/proxy-image?url=${encodeURIComponent(joueur.infos.photo)}`;
    }
    return '';
  };

  // ── Shared styles ──────────────────────────────────────────
  // cardBase est désormais importé du design system partagé (front/src/theme/designSystem.js)
  // pour garantir que tous les écrans utilisent exactement le même style de carte.

  // ── Loading ────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
        <CircularProgress sx={{ color: primaryColor }} />
        <Typography variant="body2" color="text.secondary">Cargando dashboard...</Typography>
      </Box>
    );
  }

  // ── KPI Card component ─────────────────────────────────────
  const KpiCard = ({ icon: Icon, value, label, link, color, delay = 0 }) => (
    <Link to={link} style={{ textDecoration: 'none', color: 'inherit' }}>
      <Card sx={{
        ...cardBase,
        p: 0,
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          ...cardBase['&:hover'],
          transform: 'translateY(-2px)',
          '& .kpi-icon': { transform: 'scale(1.1)' },
        },
      }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a1a2e', lineHeight: 1, mb: 0.5, fontSize: '2rem' }}>
                {value}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.85rem' }}>
                {label}
              </Typography>
            </Box>
            <Box className="kpi-icon" sx={{
              width: 48, height: 48, borderRadius: '14px',
              background: alpha(color || primaryColor, 0.1),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'transform 0.2s ease',
            }}>
              <Icon sx={{ fontSize: 26, color: color || primaryColor }} />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Link>
  );

  // ── Dual Report Mini Card (2 halves) ───────────────────────
  const ReportHalf = ({ icon: Icon, title, count, players, tabIndex, color }) => (
    <Box
      onClick={() => navigate('/informes', { state: { activeTab: tabIndex } })}
      sx={{
        flex: 1,
        minWidth: 0,
        p: 2,
        cursor: 'pointer',
        transition: 'background 0.2s ease',
        '&:hover': { bgcolor: alpha(color, 0.04) },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: '12px',
          background: alpha(color, 0.1),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon sx={{ fontSize: 22, color }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a2e', lineHeight: 1 }}>
            {count}
          </Typography>
          <Typography variant="caption" sx={{
            color: 'text.secondary', fontWeight: 500,
            display: 'block',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {title}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5, minHeight: 28 }}>
        {players && players.length > 0 && players.slice(0, 4).map((p, i) => (
          <Tooltip key={p.id} title={p.name} arrow>
            <Avatar
              src={getPlayerAvatarUrl(p.id)}
              alt={p.name}
              sx={{
                width: 28, height: 28,
                border: '2px solid #fff',
                ml: i > 0 ? -1 : 0,
                cursor: 'pointer',
                transition: 'transform 0.15s',
                '&:hover': { transform: 'scale(1.15)', zIndex: 2 },
              }}
              onClick={(e) => { e.stopPropagation(); navigate(`/players/${p.id}`); }}
            />
          </Tooltip>
        ))}
        {players && players.length > 4 && (
          <Avatar sx={{
            width: 28, height: 28, ml: -1,
            bgcolor: alpha(color, 0.15), color,
            fontSize: '0.7rem', fontWeight: 700,
            border: '2px solid #fff',
          }}>
            +{players.length - 4}
          </Avatar>
        )}
      </Box>
    </Box>
  );

  const DualReportCard = ({ left, right }) => (
    <Card sx={{
      ...cardBase,
      p: 0,
      height: '100%',
      display: 'flex',
      alignItems: 'stretch',
      overflow: 'hidden',
    }}>
      <ReportHalf {...left} />
      <Box sx={{ width: '1px', bgcolor: 'rgba(0,0,0,0.06)', alignSelf: 'stretch' }} />
      <ReportHalf {...right} />
    </Card>
  );

  // ── Render ─────────────────────────────────────────────────
  return (
    <Box sx={{
      minHeight: '100vh',
      background: '#f8f9fb',
      p: { xs: 2, md: 3 },
    }}>

      {/* ─── Birthday Banner ─── */}
      {birthdayPlayers.length > 0 && (
        <Card sx={{
          ...cardBase,
          mb: 3,
          background: `linear-gradient(135deg, ${alpha(primaryColor, 0.04)} 0%, ${alpha(primaryColor, 0.08)} 100%)`,
          borderColor: alpha(primaryColor, 0.15),
          overflow: 'hidden',
          position: 'relative',
        }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: '12px',
              background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor || primaryColor})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <CakeIcon sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', flex: 1 }}>
              {birthdayPlayers.map((player, index) => (
                <Box key={player.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {index > 0 && <Box sx={{ width: '1px', height: 28, bgcolor: alpha(primaryColor, 0.2) }} />}
                  <Link to={`/players/${player.id}`} style={{ textDecoration: 'none' }}>
                    <Avatar
                      src={getPlayerAvatarUrl(player.id)}
                      alt={player.nom}
                      sx={{
                        width: 38, height: 38,
                        border: `2px solid ${primaryColor}`,
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'scale(1.1)' },
                      }}
                    />
                  </Link>
                  <Box>
                    <Link to={`/players/${player.id}`} style={{ textDecoration: 'none' }}>
                      <Typography variant="body2" sx={{
                        fontWeight: 700, color: '#1a1a2e', fontSize: '0.9rem',
                        '&:hover': { color: primaryColor },
                      }}>
                        {player.nom}
                      </Typography>
                    </Link>
                    <Typography variant="caption" sx={{ color: primaryColor, fontWeight: 600, fontSize: '0.75rem' }}>
                      {t('dashboard.birthday', { age: player.age })} !
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ─── KPI Row ─── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {restrictedType !== 'cantera' && (
          <Grid item xs={6} sm={3}>
            <KpiCard icon={PeopleIcon} value={stats.proCount} label={t('dashboard.proPlayers')} link="/jugadores?playerTypeFilter=2" color="#6366f1" />
          </Grid>
        )}
        {restrictedType !== 'pro' && (
          <Grid item xs={6} sm={3}>
            <KpiCard icon={SchoolIcon} value={stats.canteraCount} label={t('dashboard.canteraPlayers')} link="/jugadores?playerTypeFilter=1" color="#0ea5e9" />
          </Grid>
        )}
        <Grid item xs={6} sm={3}>
          <KpiCard icon={TagIcon} value={stats.bigBoardTagCount} label={t('dashboard.rankings')} link="/scouting?tab=ranking" color="#f59e0b" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard icon={VisibilityIcon} value={stats.seguimientoTagCount} label={stats.seguimientoTagCount > 1 ? t('dashboard.seguimientos') : t('dashboard.seguimiento')} link="/scouting?tab=seguimiento" color="#10b981" />
        </Grid>
      </Grid>

      {/* ─── Reports Row ─── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <DualReportCard
            left={{  icon: ReportIcon,    title: t('dashboard.newReports'),         count: stats.recentInformes.count,  players: stats.recentInformes.players,  tabIndex: 0, color: '#6366f1' }}
            right={{ icon: IntelIcon,     title: t('dashboard.newIntel'),           count: stats.recentIntel.count,     players: stats.recentIntel.players,     tabIndex: 1, color: '#ec4899' }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <DualReportCard
            left={{  icon: MedidasIcon,   title: t('dashboard.newMedidas'),         count: stats.recentMedidas.count,   players: stats.recentMedidas.players,   tabIndex: 2, color: '#0ea5e9' }}
            right={{ icon: MedicalIcon,   title: t('dashboard.newMedical'),         count: stats.recentMedical.count,   players: stats.recentMedical.players,   tabIndex: 3, color: '#ef4444' }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <DualReportCard
            left={{  icon: TechIcon,      title: t('dashboard.newTechnicalSheets'), count: stats.recentTech.count,      players: stats.recentTech.players,      tabIndex: 4, color: '#f59e0b' }}
            right={{ icon: DocumentsIcon, title: t('dashboard.newDocuments'),       count: stats.recentDocuments.count, players: stats.recentDocuments.players, tabIndex: 6, color: '#14b8a6' }}
          />
        </Grid>
      </Grid>

      {/* ─── Main Content Grid ─── */}
      <Grid container spacing={2.5}>

        {/* ─── Left Column ─── */}
        <Grid item xs={12} md={8}>

          {/* Top Players of the Day */}
          {topGlobal.length > 0 && (
            <Card sx={{ ...cardBase, mb: 2.5, overflow: 'hidden' }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{
                  p: 2.5, pb: 2,
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  borderBottom: '1px solid rgba(0,0,0,0.05)',
                }}>
                  <SportsBasketball sx={{ fontSize: 24, color: primaryColor }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: '1.05rem' }}>
                    {t('dashboard.topPlayersOfDay')}
                  </Typography>
                  <Chip
                    size="small"
                    icon={<TrendingUp sx={{ fontSize: '14px !important' }} />}
                    label={(() => {
                      const yesterday = new Date();
                      yesterday.setDate(yesterday.getDate() - 1);
                      return yesterday.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
                    })()}
                    sx={{
                      ml: 'auto',
                      height: 26,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      bgcolor: alpha(primaryColor, 0.08),
                      color: primaryColor,
                      '& .MuiChip-icon': { color: primaryColor },
                    }}
                  />
                </Box>
                <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                  {topGlobal.slice(0, 8).map((item) => {
                    const avatarUrl = getTopPlayerAvatar(item.joueur);
                    return (
                      <Tooltip
                        key={item.type_top10}
                        arrow
                        enterDelay={200}
                        enterNextDelay={200}
                        leaveDelay={200}
                        disableInteractive={false}
                        placement="bottom"
                        slotProps={{
                          popper: { sx: { pointerEvents: 'auto' } },
                        }}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: 'transparent',
                              p: 0,
                              maxWidth: 280,
                              '& .MuiTooltip-arrow': { color: '#667eea' },
                            },
                          },
                        }}
                        title={item.joueur ? (
                          <Box sx={{
                            p: 1,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: '6px',
                            boxShadow: '0 3px 10px rgba(0,0,0,0.2)'
                          }}>
                            {/* Header avec photo et nom */}
                            <Box sx={{
                              borderBottom: '1px solid rgba(255,255,255,0.3)',
                              pb: 1, mb: 1,
                              display: 'flex', alignItems: 'center', gap: 1.5
                            }}>
                              <Avatar
                                src={avatarUrl}
                                alt={item.joueur.name}
                                sx={{
                                  width: 60, height: 60,
                                  border: '2px solid rgba(255,255,255,0.9)',
                                  boxShadow: '0 3px 8px rgba(0,0,0,0.3)',
                                  flexShrink: 0,
                                  '& img': { objectFit: 'cover', width: '100%', height: '100%' }
                                }}
                              />
                              <Typography variant="body1" sx={{
                                color: '#fff', fontWeight: 'bold',
                                fontSize: '1.1rem', textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                flex: 1
                              }}>
                                {item.joueur.name}
                              </Typography>
                            </Box>

                            {/* Infos joueur */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {item.joueur.infos?.age && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#87ceeb' }} />
                                  <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.75rem' }}>
                                    <strong>{t('dashboard.playerTooltip.age')}:</strong> {item.joueur.infos.age} {t('dashboard.playerTooltip.years')}
                                  </Typography>
                                </Box>
                              )}
                              {item.joueur.infos?.equipe && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ff6b6b' }} />
                                  <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.75rem' }}>
                                    <strong>{t('dashboard.playerTooltip.team')}:</strong> {item.joueur.infos.equipe}
                                  </Typography>
                                </Box>
                              )}
                              {item.ligue && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4ecdc4' }} />
                                  <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.75rem' }}>
                                    <strong>{t('dashboard.playerTooltip.league')}:</strong> {item.ligue}
                                  </Typography>
                                </Box>
                              )}
                              {item.joueur.infos?.adversaire && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ff9f40' }} />
                                  <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.75rem' }}>
                                    <strong>{t('dashboard.playerTooltip.against')}:</strong> {item.joueur.infos.adversaire}
                                  </Typography>
                                </Box>
                              )}
                              {item.joueur.infos?.score && (
                                <Box sx={{
                                  display: 'flex', alignItems: 'center', gap: 0.5,
                                  mt: 0.5, pt: 0.5,
                                  borderTop: '1px solid rgba(255,255,255,0.2)'
                                }}>
                                  <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.75rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <strong>{t('dashboard.playerTooltip.result')}:</strong>
                                    <Box component="span" sx={{ ml: 0.5 }}>{item.joueur.infos.score}</Box>
                                  </Typography>
                                </Box>
                              )}

                              {/* Lien vers le match */}
                              {item.joueur.infos?.url_match && (
                                <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>
                                  <Button
                                    href={item.joueur.infos.url_match}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="small"
                                    variant="contained"
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                      backgroundColor: 'rgba(255, 215, 0, 0.9)',
                                      color: '#000', fontWeight: 'bold',
                                      fontSize: '0.65rem', py: 0.3, px: 1, minHeight: 'auto',
                                      '&:hover': { backgroundColor: 'rgba(255, 215, 0, 1)', transform: 'scale(1.02)' }
                                    }}
                                  >
                                    📊 {t('dashboard.playerTooltip.viewMatch')}
                                  </Button>
                                </Box>
                              )}
                            </Box>

                            {/* Valor destacado */}
                            <Box sx={{
                              mt: 1, pt: 1,
                              borderTop: '1px solid rgba(255,255,255,0.3)',
                              display: 'flex', flexDirection: 'column',
                              alignItems: 'center', gap: 0.5
                            }}>
                              <Typography variant="caption" sx={{
                                color: '#fff', fontSize: '0.75rem', opacity: 0.9,
                                textTransform: 'uppercase', letterSpacing: '0.5px'
                              }}>
                                {typeLabels[item.type_top10]}
                              </Typography>
                              <Typography variant="body1" sx={{
                                color: '#ffd700', fontWeight: 'bold',
                                fontSize: '1.1rem', textShadow: '0 1px 3px rgba(0,0,0,0.3)'
                              }}>
                                {item.joueur.value}
                              </Typography>
                            </Box>

                            {/* Estadísticas detalladas */}
                            {item.joueur.stats_detail && (
                              <Box sx={{
                                mt: 1, pt: 1,
                                borderTop: '1px solid rgba(255,255,255,0.3)',
                                display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 0.8
                              }}>
                                <Typography variant="caption" sx={{
                                  color: '#fff', fontSize: '0.75rem', gridColumn: 'span 2',
                                  textAlign: 'center', fontWeight: 'bold', mb: 0.5,
                                  textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.9
                                }}>
                                  {t('dashboard.playerTooltip.otherStats')}
                                </Typography>

                                {item.joueur.stats_detail.minutes != null && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#9b59b6' }} />
                                    <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.7rem' }}>
                                      <strong>{t('dashboard.playerTooltip.minutes')}:</strong> {item.joueur.stats_detail.minutes}
                                    </Typography>
                                  </Box>
                                )}
                                {item.joueur.stats_detail.points > 0 && item.type_top10 !== 'POINTS' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ff6b6b' }} />
                                    <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.7rem' }}>
                                      <strong>Pts:</strong> {item.joueur.stats_detail.points}
                                    </Typography>
                                  </Box>
                                )}
                                {item.joueur.stats_detail.rebonds > 0 && item.type_top10 !== 'REBONDS' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4ecdc4' }} />
                                    <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.7rem' }}>
                                      <strong>Reb:</strong> {item.joueur.stats_detail.rebonds}
                                    </Typography>
                                  </Box>
                                )}
                                {item.joueur.stats_detail.passes > 0 && item.type_top10 !== 'PASSES' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#45b7d1' }} />
                                    <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.7rem' }}>
                                      <strong>Ast:</strong> {item.joueur.stats_detail.passes}
                                    </Typography>
                                  </Box>
                                )}
                                {item.joueur.stats_detail.steals > 0 && item.type_top10 !== 'STEALS' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#96ceb4' }} />
                                    <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.7rem' }}>
                                      <strong>Stl:</strong> {item.joueur.stats_detail.steals}
                                    </Typography>
                                  </Box>
                                )}
                                {item.joueur.stats_detail.contres > 0 && item.type_top10 !== 'BLOCKS' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#dda0dd' }} />
                                    <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.7rem' }}>
                                      <strong>Blk:</strong> {item.joueur.stats_detail.contres}
                                    </Typography>
                                  </Box>
                                )}
                                {item.joueur.stats_detail.three_pm > 0 && item.type_top10 !== 'THREE_PM' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ffa500' }} />
                                    <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.7rem' }}>
                                      <strong>3pm:</strong> {item.joueur.stats_detail.three_pm}
                                    </Typography>
                                  </Box>
                                )}
                                {item.joueur.stats_detail.evaluation > 0 && item.type_top10 !== 'EVALUATION' && (
                                  <Box sx={{
                                    gridColumn: 'span 2', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', gap: 0.5,
                                    mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(255,255,255,0.2)'
                                  }}>
                                    <Typography variant="caption" sx={{ color: '#ffd700', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                      <strong>{t('dashboard.playerTooltip.evaluation')}:</strong> {item.joueur.stats_detail.evaluation}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        ) : ''}
                      >
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: '12px',
                            border: '1px solid rgba(0,0,0,0.06)',
                            // Les utilisateurs cantera gardent la vue mais pas le lien vers la fiche
                            cursor: restrictedType === 'cantera' ? 'default' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            transition: 'all 0.2s ease',
                            bgcolor: hoveredStat === item.type_top10 ? alpha(primaryColor, 0.04) : 'transparent',
                            '&:hover': {
                              bgcolor: alpha(primaryColor, 0.04),
                              borderColor: alpha(primaryColor, 0.15),
                            },
                          }}
                          onMouseEnter={() => setHoveredStat(item.type_top10)}
                          onMouseLeave={() => setHoveredStat(null)}
                          onClick={restrictedType === 'cantera' ? undefined : () => {
                            const url = item.joueur?.infos?.local_id
                              ? `/players/${item.joueur.infos.local_id}`
                              : item.joueur?.infos?.url;
                            if (url) {
                              if (url.startsWith('/')) navigate(url);
                              else window.open(url, '_blank');
                            }
                          }}
                        >
                          <Avatar
                            src={avatarUrl}
                            alt={item.joueur?.name}
                            sx={{
                              width: 44, height: 44,
                              border: `2px solid ${alpha(primaryColor, 0.2)}`,
                              '& img': { objectFit: 'cover', width: '100%', height: '100%' }
                            }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" sx={{
                              color: primaryColor, fontWeight: 700,
                              fontSize: '0.65rem', textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}>
                              {typeLabels[item.type_top10]}
                            </Typography>
                            <Typography variant="body2" sx={{
                              fontWeight: 700, color: '#1a1a2e',
                              fontSize: '0.85rem', lineHeight: 1.2,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              {item.joueur?.name || '-'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                              {item.joueur?.infos?.equipe || item.ligue}
                            </Typography>
                          </Box>
                          <Typography variant="h6" sx={{
                            fontWeight: 800, color: primaryColor,
                            fontSize: '1.1rem', flexShrink: 0,
                          }}>
                            {item.joueur?.value || '-'}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Video Player */}
          {videos.length > 0 && (
            <Card sx={{ ...cardBase, overflow: 'hidden' }}>
              <Box sx={{
                p: 2, pb: 1.5,
                display: 'flex', alignItems: 'center', gap: 1.5,
                borderBottom: '1px solid rgba(0,0,0,0.05)',
              }}>
                <PlayCircleOutline sx={{ fontSize: 22, color: primaryColor }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: '1.05rem' }}>
                  {t('dashboard.latestVideos')}
                </Typography>
                <Chip
                  size="small"
                  label={`${currentVideoIndex + 1} / ${videos.length}`}
                  sx={{
                    height: 24, fontSize: '0.75rem', fontWeight: 600,
                    bgcolor: alpha(primaryColor, 0.08), color: primaryColor,
                  }}
                />
                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => setCurrentVideoIndex((prev) => prev > 0 ? prev - 1 : videos.length - 1)}
                    sx={{ bgcolor: 'rgba(0,0,0,0.04)', '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' } }}
                  >
                    <NavigateBefore sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setCurrentVideoIndex((prev) => (prev + 1) % videos.length)}
                    sx={{ bgcolor: 'rgba(0,0,0,0.04)', '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' } }}
                  >
                    <NavigateNext sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Video Header Info */}
              <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: 'rgba(0,0,0,0.01)' }}>
                <Link to={`/players/${videos[currentVideoIndex].player_id}`} state={{ activeTab: 'video' }}>
                  <Avatar
                    src={getPlayerAvatarUrl(videos[currentVideoIndex].player_id)}
                    sx={{ width: 40, height: 40, border: `2px solid ${alpha(primaryColor, 0.3)}` }}
                  />
                </Link>
                <Box sx={{ flex: 1 }}>
                  <Link to={`/players/${videos[currentVideoIndex].player_id}`} state={{ activeTab: 'video' }} style={{ textDecoration: 'none' }}>
                    <Typography variant="body2" sx={{
                      fontWeight: 700, color: '#1a1a2e', fontSize: '0.9rem',
                      '&:hover': { color: primaryColor },
                    }}>
                      {videos[currentVideoIndex].player_name}
                      {(() => {
                        const age = parseFloat(videos[currentVideoIndex].player_age_decimal);
                        return !isNaN(age) ? ` (${age.toFixed(1)})` : '';
                      })()}
                    </Typography>
                  </Link>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                    <Chip size="small" label={videos[currentVideoIndex].player_position} sx={{
                      height: 20, fontSize: '0.65rem', fontWeight: 600,
                      bgcolor: alpha(primaryColor, 0.1), color: primaryColor,
                    }} />
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.7rem' }}>
                    <CalendarToday sx={{ fontSize: 12 }} />
                    {new Date(videos[currentVideoIndex].fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </Typography>
                </Box>
              </Box>

              {/* Video Player */}
              <Box sx={{ position: 'relative', pt: '56.25%', bgcolor: '#000' }}>
                <ReactPlayer
                  url={videos[currentVideoIndex].url}
                  controls
                  width="100%"
                  height="100%"
                  style={{ position: 'absolute', top: 0, left: 0 }}
                />
              </Box>

              {/* Video Title */}
              <Box sx={{ p: 2, pt: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>
                  {videos[currentVideoIndex].titre}
                </Typography>
                {videos[currentVideoIndex].detalles && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    dangerouslySetInnerHTML={{ __html: videos[currentVideoIndex].detalles }}
                    sx={{ display: 'block', fontSize: '0.75rem', lineHeight: 1.5, maxHeight: 40, overflow: 'hidden' }}
                  />
                )}
              </Box>
            </Card>
          )}
        </Grid>

        {/* ─── Right Column ─── */}
        <Grid item xs={12} md={4}>

          {/* News Feed */}
          {news.length > 0 && (
            <Card sx={{ ...cardBase, mb: 2.5, overflow: 'hidden' }}>
              <Box sx={{
                p: 2, pb: 1.5,
                display: 'flex', alignItems: 'center', gap: 1,
                borderBottom: '1px solid rgba(0,0,0,0.05)',
              }}>
                <NewsIcon sx={{ fontSize: 20, color: primaryColor }} />
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: '0.95rem' }}>
                  {t('dashboard.latestNews')}
                </Typography>
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {currentNewsIndex + 1}/{news.length}
                  </Typography>
                  <IconButton size="small" onClick={() => setCurrentNewsIndex((prev) => prev > 0 ? prev - 1 : news.length - 1)}>
                    <NavigateBefore sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => setCurrentNewsIndex((prev) => (prev + 1) % news.length)}>
                    <NavigateNext sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* News items - show current + next 2 */}
              {[0, 1, 2].map((offset) => {
                const idx = (currentNewsIndex + offset) % news.length;
                const item = news[idx];
                if (!item) return null;
                const isMain = offset === 0;

                return (
                  <Box
                    key={`${idx}-${offset}`}
                    sx={{
                      p: 2,
                      borderBottom: offset < 2 ? '1px solid rgba(0,0,0,0.04)' : 'none',
                      transition: 'background 0.2s',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.015)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      {item.player_id && (
                        <Link to={`/players/${item.player_id}`}>
                          <Avatar
                            src={getPlayerAvatarUrl(item.player_id)}
                            sx={{
                              width: isMain ? 44 : 36,
                              height: isMain ? 44 : 36,
                              border: `2px solid ${alpha(primaryColor, 0.2)}`,
                              flexShrink: 0,
                            }}
                          />
                        </Link>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          {item.player_nom && (
                            <Link to={`/players/${item.player_id}`} style={{ textDecoration: 'none' }}>
                              <Typography variant="caption" sx={{
                                fontWeight: 700, color: primaryColor,
                                '&:hover': { textDecoration: 'underline' },
                              }}>
                                {item.player_nom}
                              </Typography>
                            </Link>
                          )}
                          <Typography variant="caption" sx={{ ml: 'auto', color: 'text.disabled', fontSize: '0.65rem', flexShrink: 0 }}>
                            {new Date(item.updated_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{
                          fontWeight: 600, color: '#1a1a2e',
                          fontSize: isMain ? '0.85rem' : '0.8rem',
                          lineHeight: 1.4,
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          display: '-webkit-box', WebkitLineClamp: isMain ? 2 : 1, WebkitBoxOrient: 'vertical',
                        }}>
                          {item.title}
                        </Typography>
                        {isMain && (
                          <Typography variant="caption" sx={{
                            color: 'text.secondary', fontSize: '0.72rem',
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                            overflow: 'hidden', lineHeight: 1.5, mt: 0.5,
                          }}>
                            {item.content.replace(/<[^>]*>/g, '')}
                          </Typography>
                        )}
                        {isMain && item.url && (
                          <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                            <Typography variant="caption" sx={{
                              color: primaryColor, fontSize: '0.7rem', fontWeight: 600,
                              display: 'inline-flex', alignItems: 'center', gap: 0.3, mt: 0.5,
                              '&:hover': { textDecoration: 'underline' },
                            }}>
                              {t('dashboard.readMore')} <OpenInNewIcon sx={{ fontSize: 10 }} />
                            </Typography>
                          </a>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}

              {/* Progress bar for auto-advance */}
              <LinearProgress
                variant="determinate"
                value={((currentNewsIndex + 1) / news.length) * 100}
                sx={{
                  height: 2,
                  bgcolor: 'rgba(0,0,0,0.04)',
                  '& .MuiLinearProgress-bar': { bgcolor: primaryColor, transition: 'transform 0.3s ease' },
                }}
              />
            </Card>
          )}

          {/* Photo Gallery */}
          <Card sx={{ ...cardBase, mb: 2.5, overflow: 'hidden' }}>
            <Box sx={{ position: 'relative', height: 220, bgcolor: '#f0f0f0' }}>
              {canteraPhotos.length > 0 && (
                <>
                  <Box
                    component="img"
                    src={canteraPhotos[currentSlide]?.photo}
                    alt={`Summit ${canteraPhotos[currentSlide]?.id}`}
                    sx={{
                      width: '100%', height: '100%',
                      objectFit: 'cover',
                      transition: 'opacity 0.5s ease',
                    }}
                  />
                  <Box sx={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: '40%',
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.4))',
                  }} />
                  <Box sx={{ position: 'absolute', bottom: 12, left: 12, right: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <IconButton
                      size="small"
                      onClick={() => setCurrentSlide((prev) => prev > 0 ? prev - 1 : canteraPhotos.length - 1)}
                      sx={{ bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: '#fff' }, width: 32, height: 32 }}
                    >
                      <NavigateBefore sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {canteraPhotos.slice(0, 8).map((_, i) => (
                        <Box key={i} sx={{
                          width: 6, height: 6, borderRadius: '50%',
                          bgcolor: i === currentSlide % 8 ? '#fff' : 'rgba(255,255,255,0.4)',
                          transition: 'all 0.2s',
                        }} />
                      ))}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => setCurrentSlide((prev) => (prev + 1) % canteraPhotos.length)}
                      sx={{ bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: '#fff' }, width: 32, height: 32 }}
                    >
                      <NavigateNext sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </>
              )}
            </Box>
          </Card>

          {/* Club Logo */}
          <Card sx={{
            ...cardBase,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            p: 3, minHeight: 180,
          }}>
            <Box
              component="img"
              src={dashboardLogoPath}
              alt="Club Logo"
              sx={{
                maxWidth: '100%', maxHeight: 160,
                objectFit: 'contain',
                opacity: 0.85,
                filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))',
              }}
            />
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
